import React, { useState, useRef } from 'react';
import { Modal, Select, Switch, Button, Popover, Empty, Spin, Tag, message } from 'antd';
import { HolderOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import apiClient from '../../../../api/apiClient';
import { registrarBitacora } from '../../../../utils/bitacora';

const { Option } = Select;

const ordenDe = (c) => c.OrdenReporte ?? c.NoOrden;

// Visibles primero (por su orden), ocultos al final (también por su orden)
const ordenarInicial = (cursos) => {
  const porOrden = [...cursos].sort((a, b) => ordenDe(a) - ordenDe(b));
  return [...porOrden.filter(c => c.MostrarEnReporte), ...porOrden.filter(c => !c.MostrarEnReporte)];
};

const ayuda = (
  <div style={{ maxWidth: 280 }}>
    <p style={{ margin: '0 0 8px' }}>
      Arrastrá los cursos <b>azules</b> y soltalos en la posición deseada. El número de la
      izquierda es la columna en que aparecerá en el Cuadro de Promedios.
    </p>
    <p style={{ margin: '0 0 8px' }}>
      Apagá <b>Mostrar en reportes</b> para ocultar un curso: se va al final en gris y no
      aparece en el reporte. Encendelo de nuevo para que vuelva a la lista.
    </p>
    <p style={{ margin: 0 }}>Al terminar, presioná <b>Guardar orden</b>.</p>
  </div>
);

const OrdenCursosModal = ({ visible, grados, onCancel, onSuccess }) => {
  const [idGrado, setIdGrado] = useState(null);
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [sobre, setSobre] = useState(null);   // índice sobre el que se está arrastrando
  const arrastrando = useRef(null);

  const visibles = lista.filter(c => c.MostrarEnReporte).length;
  const nombreGrado = grados.find(g => g.IdGrado === idGrado)?.NombreGrado;

  const cargarCursos = async (grado) => {
    setIdGrado(grado);
    setLista([]);
    if (!grado) return;
    setCargando(true);
    try {
      const response = await apiClient.get(`/cursos/grado/${grado}`);
      if (response.data.success) setLista(ordenarInicial(response.data.data));
    } catch (error) {
      if (error.response?.status !== 404) message.error('Error al cargar los cursos del grado');
    } finally {
      setCargando(false);
    }
  };

  const mover = (desde, hasta) => {
    if (desde === null || desde === hasta) return;
    setLista(prev => {
      const nueva = [...prev];
      const [curso] = nueva.splice(desde, 1);
      nueva.splice(hasta, 0, curso);
      return nueva;
    });
  };

  const cambiarVisible = (idCurso, mostrar) => {
    setLista(prev => {
      const curso = { ...prev.find(c => c.idCurso === idCurso), MostrarEnReporte: mostrar };
      const resto = prev.filter(c => c.idCurso !== idCurso);
      const finVisibles = resto.filter(c => c.MostrarEnReporte).length;
      // Al ocultarlo va al final; al mostrarlo, al final de los visibles
      mostrar ? resto.splice(finVisibles, 0, curso) : resto.push(curso);
      return resto;
    });
  };

  const cerrar = () => {
    setIdGrado(null);
    setLista([]);
    onCancel();
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const payload = {
        idGrado,
        cursos: lista.map((c, i) => ({
          idCurso: c.idCurso,
          OrdenReporte: i + 1,
          MostrarEnReporte: Boolean(c.MostrarEnReporte),
        })),
      };
      const response = await apiClient.put('/cursos/orden-reporte', payload);
      if (response.data.success) {
        await registrarBitacora('Cambio de Orden de Cursos', `Grado: ${nombreGrado} (ID ${idGrado})`);
        message.success('Orden guardado exitosamente');
        setIdGrado(null);
        setLista([]);
        onSuccess();
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al guardar el orden');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      title="Cambiar orden de cursos en reportes"
      open={visible}
      onCancel={cerrar}
      width={640}
      destroyOnClose
      footer={[
        <Button key="cancelar" onClick={cerrar}>Cancelar</Button>,
        <Button key="guardar" type="primary" loading={guardando} disabled={!lista.length} onClick={guardar}>
          Guardar orden
        </Button>,
      ]}
    >
      <Select
        placeholder="Seleccione el grado"
        style={{ width: '100%', marginBottom: 16 }}
        value={idGrado}
        onChange={cargarCursos}
        showSearch
        optionFilterProp="children"
      >
        {grados.map(g => (
          <Option key={g.IdGrado} value={g.IdGrado}>{g.NombreGrado}</Option>
        ))}
      </Select>

      {idGrado && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{nombreGrado}</h3>
          <Popover content={ayuda} title="¿Cómo cambiar el orden?" trigger="click" placement="bottomRight">
            <Button icon={<QuestionCircleOutlined />}>Ayuda</Button>
          </Popover>
        </div>
      )}

      <Spin spinning={cargando}>
        {idGrado && !cargando && !lista.length && <Empty description="Este grado no tiene cursos activos" />}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '55vh', overflowY: 'auto' }}>
          {lista.map((curso, i) => {
            const activo = Boolean(curso.MostrarEnReporte);
            return (
              <div
                key={curso.idCurso}
                draggable={activo}
                onDragStart={() => { arrastrando.current = i; }}
                onDragOver={(e) => {
                  if (arrastrando.current === null || !activo) return;
                  e.preventDefault();
                  setSobre(i);
                }}
                onDrop={(e) => { e.preventDefault(); mover(arrastrando.current, i); }}
                onDragEnd={() => { arrastrando.current = null; setSobre(null); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: `1px solid ${sobre === i ? '#1677ff' : activo ? '#91caff' : '#d9d9d9'}`,
                  background: activo ? '#e6f4ff' : '#f5f5f5',
                  color: activo ? 'inherit' : '#8c8c8c',
                  cursor: activo ? 'grab' : 'default',
                  userSelect: 'none',
                }}
              >
                <HolderOutlined style={{ color: activo ? '#1677ff' : '#d9d9d9' }} />
                <Tag color={activo ? 'blue' : 'default'} style={{ minWidth: 34, textAlign: 'center', margin: 0 }}>
                  {activo ? i + 1 : '—'}
                </Tag>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>{curso.Curso}</div>
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                    SIRE: {curso.CodigoSire ?? 'Sin código'}
                  </div>
                </div>
                <span style={{ fontSize: 12 }}>Mostrar en reportes</span>
                <Switch
                  size="small"
                  checked={activo}
                  onChange={(val) => cambiarVisible(curso.idCurso, val)}
                />
              </div>
            );
          })}
        </div>

        {lista.length > 0 && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#8c8c8c' }}>
            {visibles} de {lista.length} cursos se muestran en el reporte
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default OrdenCursosModal;
